import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ClipboardCheck, Target, TrendingUp } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

type Props = {
  readonly open: boolean
  onOpenChange: (open: boolean) => void
}

export function MandatoryAssessmentModal({ open, onOpenChange }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  function handleTakeAssessment() {
    onOpenChange(false)
    navigate('/performance')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg bg-white'>
        <DialogHeader className='space-y-3'>
          <div className='mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-interaction to-accent-purple'>
            <ClipboardCheck className='h-7 w-7 text-white' strokeWidth={2} />
          </div>
          <DialogTitle className='text-center text-xl'>
            {t('mandatoryAssessment.title', 'Take your English Proficiency Assessment')}
          </DialogTitle>
          <DialogDescription className='text-center text-base'>
            {t(
              'mandatoryAssessment.description',
              "Your employer or organization requires you to take EnGen's Proficiency Assessment."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className='my-4 space-y-3'>
          <div className='flex items-start gap-3 rounded-lg bg-slate-50 p-3'>
            <div className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100'>
              <Target className='h-4 w-4 text-accent-purple' />
            </div>
            <div>
              <p className='font-medium text-slate-900'>
                {t('mandatoryAssessment.benefit1Title', 'Personalized Learning')}
              </p>
              <p className='text-sm text-slate-600'>
                {t(
                  'mandatoryAssessment.benefit1Description',
                  'The Proficiency Assessment helps us give you course recommendations and practice activities at the right level for you.'
                )}
              </p>
            </div>
          </div>

          <div className='flex items-start gap-3 rounded-lg bg-slate-50 p-3'>
            <div className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-100'>
              <TrendingUp className='h-4 w-4 text-green-600' />
            </div>
            <div>
              <p className='font-medium text-slate-900'>
                {t('mandatoryAssessment.benefit2Title', 'Track Your Progress')}
              </p>
              <p className='text-sm text-slate-600'>
                {t(
                  'mandatoryAssessment.benefit2Description',
                  'Know your English level now so you can see how your English improves over time!'
                )}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className='flex-col gap-2 sm:flex-col'>
          <p className='text-center text-sm text-slate-600'>
            {t(
              'mandatoryAssessment.instruction',
              "Click the button below to take EnGen's English Proficiency Assessment."
            )}
          </p>
          <Button
            onClick={handleTakeAssessment}
            className='w-full bg-interaction hover:bg-interaction-active'
            size='lg'
          >
            {t('mandatoryAssessment.takeAssessmentButton', 'Take Proficiency Assessment')}
          </Button>
          <p className='text-center text-xs text-muted-foreground'>
            {t('mandatoryAssessment.reminder', 'This reminder will appear until you complete the assessment')}
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
